import { safe, state } from './config'
import { isActive, isIdle } from './journey'
import { clampInt } from './scrub'
import { record, reserve } from './transport'

const IDLE_AFTER_MS = 30000
const DAY_MS = 86400000
const PERF_MAX_MS = 600000
const PERF_LOAD_DELAY_MS = 10000
const NAV_TYPES = new Set(['navigate', 'reload', 'back_forward', 'prerender'])
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart']

let metrics = freshMetrics()
let accountedAt = Date.now()
let hidden = false
let lcp = 0
let documentCls = 0
let perfLoad = null
let perfTimer = null
let scrollFrame = 0
let scrollTarget = null

function freshMetrics() {
  return { active: 0, hidden: 0, idle: 0, maxScroll: null, longTasks: 0, longTaskMs: 0, inp: 0, cls: 0 }
}

function navigationEntry() {
  return window.performance?.getEntriesByType?.('navigation')?.[0] ?? null
}

export function navigationType() {
  const entry = navigationEntry()

  if (entry?.activationStart > 0) {
    return 'prerender'
  }

  return NAV_TYPES.has(entry?.type) ? entry.type : 'navigate'
}

// Visible time counts as active until 30 s after the last pointer, key, wheel, touch or scroll input.
function account(now = Date.now()) {
  const elapsed = now - accountedAt
  const from = accountedAt

  accountedAt = now

  if (elapsed <= 0) {
    return
  }

  if (hidden) {
    metrics.hidden += elapsed

    return
  }

  const active = Math.min(Math.max(Math.min(now, state.lastInputAt + IDLE_AFTER_MS) - from, 0), elapsed)

  metrics.active += active
  metrics.idle += elapsed - active
}

function onActivity() {
  const now = Date.now()

  account(now)

  // Measured before this input refreshes the clock: the click that follows reserves events and would hide the gap.
  if (isIdle(now)) {
    state.idleGap = true
  }

  state.lastInputAt = now
}

function measureScroll(target) {
  const el = target instanceof Element ? target : (document.scrollingElement ?? document.documentElement)

  if (!el) {
    return
  }

  const view = el.clientHeight
  const total = el.scrollHeight

  // Small inner scrollers (dropdowns, lists) say nothing about how far the page was read.
  if (total <= view + 1 || view < window.innerHeight * 0.4) {
    return
  }

  metrics.maxScroll = Math.max(metrics.maxScroll ?? 0, Math.min(Math.round(((el.scrollTop + view) / total) * 100), 100))
}

function onScroll(event) {
  onActivity()
  scrollTarget = event.target

  if (!scrollFrame) {
    scrollFrame = window.requestAnimationFrame(
      safe(() => {
        scrollFrame = 0
        measureScroll(scrollTarget)
      })
    )
  }
}

export function noteVisibility() {
  account()
  hidden = document.visibilityState === 'hidden'
}

export function startPageMetrics() {
  account()
  metrics = freshMetrics()
  state.lastInputAt = Date.now()
}

export function takePageMetrics() {
  account()

  return {
    active_ms: clampInt(metrics.active, 0, DAY_MS),
    hidden_ms: clampInt(metrics.hidden, 0, DAY_MS),
    idle_ms: clampInt(metrics.idle, 0, DAY_MS),
    max_scroll_pct: metrics.maxScroll === null ? undefined : clampInt(metrics.maxScroll, 0, 100),
    long_tasks: clampInt(metrics.longTasks, 0, 100000),
    long_task_ms: clampInt(metrics.longTaskMs, 0, DAY_MS),
    inp_ms: metrics.inp > 0 ? clampInt(metrics.inp, 0, 60000) : undefined,
    cls_milli: clampInt(metrics.cls * 1000, 0, 100000),
  }
}

function positive(value) {
  return Number.isFinite(value) && value > 0 ? clampInt(value, 0, PERF_MAX_MS) : undefined
}

export function schedulePerfLoad(snap) {
  perfLoad = snap
  perfTimer = window.setTimeout(safe(emitPerfLoad), PERF_LOAD_DELAY_MS)
}

export function emitPerfLoad() {
  const snap = perfLoad

  perfLoad = null
  window.clearTimeout(perfTimer)

  const entry = navigationEntry()

  if (!snap || !entry || !isActive()) {
    return
  }

  const start = entry.activationStart || 0
  const fcp = window.performance.getEntriesByName?.('first-contentful-paint')?.[0]?.startTime
  const resources = window.performance
    .getEntriesByType('resource')
    .reduce((sum, resource) => sum + (resource.transferSize || 0), 0)

  record(
    'perf.load',
    {
      target: snap.component,
      props: {
        ttfb_ms: positive(entry.responseStart - start),
        fcp_ms: positive(fcp - start),
        lcp_ms: positive(lcp - start),
        dcl_ms: positive(entry.domContentLoadedEventEnd - start),
        load_ms: positive(entry.loadEventEnd - start),
        cls_milli: clampInt(documentCls * 1000, 0, 100000),
        transfer_kb: clampInt(((entry.transferSize || 0) + resources) / 1024, 0, 1000000),
        nav_type: navigationType(),
      },
    },
    reserve(snap)
  )
}

function observe(type, callback, options = {}) {
  try {
    if (!window.PerformanceObserver?.supportedEntryTypes?.includes(type)) {
      return
    }

    new window.PerformanceObserver(safe((list) => callback(list.getEntries()))).observe({
      type,
      buffered: true,
      ...options,
    })
  } catch {
    return
  }
}

export function installPerformance() {
  const options = { capture: true, passive: true }

  accountedAt = Date.now()
  hidden = document.visibilityState === 'hidden'
  ACTIVITY_EVENTS.forEach((type) => document.addEventListener(type, safe(onActivity), options))
  document.addEventListener('scroll', safe(onScroll), options)

  observe('largest-contentful-paint', (entries) => {
    const last = entries[entries.length - 1]

    lcp = last ? last.renderTime || last.loadTime || last.startTime : lcp
  })
  observe('layout-shift', (entries) => {
    entries.forEach((entry) => {
      if (!entry.hadRecentInput) {
        metrics.cls += entry.value

        if (perfLoad) {
          documentCls += entry.value
        }
      }
    })
  })
  observe('longtask', (entries) => {
    entries.forEach((entry) => {
      metrics.longTasks += 1
      metrics.longTaskMs += entry.duration
    })
  })
  observe(
    'event',
    (entries) => {
      entries.forEach((entry) => {
        if (entry.interactionId > 0) {
          metrics.inp = Math.max(metrics.inp, entry.duration)
        }
      })
    },
    { durationThreshold: 40 }
  )
}
